// Who gets the no-consent-banner analytics tier.
//
// EU/EEA, UK and Swiss law (ePrivacy Art. 5(3), GDPR, UK PECR, Swiss FADP)
// make storing anything on the device for analytics an opt-in matter, so
// visitors there get the COOKIELESS tier (src/utils/analytics.ts
// posthogConfig). Everyone else gets full product analytics (cookies, session
// replay, retention) -- no banner either way.
//
// The region comes from the browser's IANA time zone: free, synchronous (it
// decides before PostHog loads) and needs no network or IP lookup. It is
// CONSERVATIVE: a missing / unreadable zone counts as restricted, and so does
// every Europe/* zone (incl. non-EU Europe). Travellers and VPN users can be
// misclassified; a visitor whose clock is set to Europe is treated as European.
//
// index.html's GA snippet runs before the bundle and repeats this test inline
// (consentRegion.test.ts keeps the two in lockstep).

/** Whole-prefix zones: all of Europe (+ Arctic/Longyearbyen = Svalbard, Norway). */
export const RESTRICTED_ZONE_PREFIX = /^(Europe|Arctic)\//;

/** EU/EEA territories whose zones sit outside Europe/*: the Atlantic islands,
 *  Cyprus, and the French outermost regions. */
export const RESTRICTED_ZONES = new Set([
    'Atlantic/Reykjavik', 'Atlantic/Canary', 'Atlantic/Madeira', 'Atlantic/Azores', 'Atlantic/Faroe',
    'Asia/Nicosia', 'Asia/Famagusta',
    'America/Martinique', 'America/Guadeloupe', 'America/Cayenne', 'America/St_Barthelemy', 'America/Marigot',
    'Indian/Reunion', 'Indian/Mayotte',
]);

export function isRestrictedZone(zone: string | null | undefined): boolean {
    if (!zone) return true;
    return RESTRICTED_ZONE_PREFIX.test(zone) || RESTRICTED_ZONES.has(zone);
}

/** The browser's time zone, or '' when it cannot be read. */
export function browserTimeZone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch {
        return '';
    }
}

/**
 * ISO country of every consent-region zone (IANA zone.tab, + common legacy
 * aliases browsers still report). In cookieless mode PostHog strips the IP
 * BEFORE GeoIP, so these visitors arrive with no country; the time zone is the
 * only location signal, and a country is not identifying. Coarse by nature
 * (one zone per country; a traveller reports their clock's country).
 */
const ZONE_COUNTRY: Record<string, string> = {
    'America/Cayenne': 'GF', 'America/Guadeloupe': 'GP', 'America/Marigot': 'MF',
    'America/Martinique': 'MQ', 'America/St_Barthelemy': 'BL', 'Arctic/Longyearbyen': 'SJ',
    'Asia/Famagusta': 'CY', 'Asia/Nicosia': 'CY', 'Atlantic/Azores': 'PT',
    'Atlantic/Canary': 'ES', 'Atlantic/Faeroe': 'FO', 'Atlantic/Faroe': 'FO',
    'Atlantic/Jan_Mayen': 'SJ', 'Atlantic/Madeira': 'PT', 'Atlantic/Reykjavik': 'IS',
    'Europe/Amsterdam': 'NL', 'Europe/Andorra': 'AD', 'Europe/Astrakhan': 'RU',
    'Europe/Athens': 'GR', 'Europe/Belfast': 'GB', 'Europe/Belgrade': 'RS',
    'Europe/Berlin': 'DE', 'Europe/Bratislava': 'SK', 'Europe/Brussels': 'BE',
    'Europe/Bucharest': 'RO', 'Europe/Budapest': 'HU', 'Europe/Busingen': 'DE',
    'Europe/Chisinau': 'MD', 'Europe/Copenhagen': 'DK', 'Europe/Dublin': 'IE',
    'Europe/Gibraltar': 'GI', 'Europe/Guernsey': 'GG', 'Europe/Helsinki': 'FI',
    'Europe/Isle_of_Man': 'IM', 'Europe/Istanbul': 'TR', 'Europe/Jersey': 'JE',
    'Europe/Kaliningrad': 'RU', 'Europe/Kiev': 'UA', 'Europe/Kirov': 'RU',
    'Europe/Kyiv': 'UA', 'Europe/Lisbon': 'PT', 'Europe/Ljubljana': 'SI',
    'Europe/London': 'GB', 'Europe/Luxembourg': 'LU', 'Europe/Madrid': 'ES',
    'Europe/Malta': 'MT', 'Europe/Mariehamn': 'AX', 'Europe/Minsk': 'BY',
    'Europe/Monaco': 'MC', 'Europe/Moscow': 'RU', 'Europe/Nicosia': 'CY',
    'Europe/Oslo': 'NO', 'Europe/Paris': 'FR', 'Europe/Podgorica': 'ME',
    'Europe/Prague': 'CZ', 'Europe/Riga': 'LV', 'Europe/Rome': 'IT',
    'Europe/Samara': 'RU', 'Europe/San_Marino': 'SM', 'Europe/Sarajevo': 'BA',
    'Europe/Saratov': 'RU', 'Europe/Simferopol': 'UA', 'Europe/Skopje': 'MK',
    'Europe/Sofia': 'BG', 'Europe/Stockholm': 'SE', 'Europe/Tallinn': 'EE',
    'Europe/Tirane': 'AL', 'Europe/Tiraspol': 'MD', 'Europe/Ulyanovsk': 'RU',
    'Europe/Uzhgorod': 'UA', 'Europe/Vaduz': 'LI', 'Europe/Vatican': 'VA',
    'Europe/Vienna': 'AT', 'Europe/Vilnius': 'LT', 'Europe/Volgograd': 'RU',
    'Europe/Warsaw': 'PL', 'Europe/Zagreb': 'HR', 'Europe/Zaporozhye': 'UA',
    'Europe/Zurich': 'CH', 'Indian/Mayotte': 'YT', 'Indian/Reunion': 'RE',
};

/** The ISO country for a consent-region zone, or `unknown`. */
export function zoneCountry(zone: string | null | undefined): string {
    return (zone && ZONE_COUNTRY[zone]) || 'unknown';
}

/** True when this visitor gets the cookieless, no-replay tier. */
export function inConsentRegion(): boolean {
    if (typeof window !== 'undefined') {
        // index.html decided first (same rule); reuse it so GA and PostHog agree.
        const decided = (window as { __pmtConsentRegion?: unknown }).__pmtConsentRegion;
        if (typeof decided === 'boolean') return decided;
    }
    return isRestrictedZone(browserTimeZone());
}
